import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { EstimateInfoForOpportunity, EstimateTypes, OpportunityModel, OpportunityRequest, OpportunityVerificationResponse, VerifyOpportunityRequest } from '../../../../models/dtos';
import { ISearchVerifyButtonState } from '../../../../models/common/ISearchVerifyButtonState';
import { ILinkUnlinkFormGroup } from '../../../../models/formGroupInterfaces/ILinkUnlinkFormGroup';
import { CrmOperationsService } from '../../../../services/crm-operations.service';
import { LinkUnlinkOpportunityEvent } from '../../../../services/api/crm-api.service';
import { MultilingualCurrencyPipe } from '../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { finalize, merge, Subject, takeUntil } from 'rxjs';
import { SearchEstimateDialogComponent } from '../search-estimate-dialog/search-estimate-dialog.component';
import { SearchOpportunityDialogComponent } from '../search-opportunity-dialog/search-opportunity-dialog.component';
import { CONSTANTS } from '../../../../Constants/Constants';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { ConfirmationDialogService } from '../../../../services/common/confirmation-dialog.service';
import {MatIconModule} from '@angular/material/icon'
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';
import { LoadingSpinnerService } from '../../../../services/common/loading-spinner.service';
import { MultilingualDatePipe } from '../../../../pipes/multilingual-date-pipe/multilingual-date-pipe';

@Component({
  selector: 'app-information-link-unlink',
  imports: [
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MultilingualCurrencyPipe,
    MatIconModule,
    MultilingualDatePipe
],
  templateUrl: './information-link-unlink.component.html',
  styleUrl: './information-link-unlink.component.scss'
})
export class InformationLinkUnlinkComponent implements OnInit, OnDestroy {
  @Output() cancel = new EventEmitter<void>();
  private _isCollaborativeReadOnly = false;
  @Input()
  set isCollaborativeReadOnly(value: boolean) {
    this._isCollaborativeReadOnly = value;
    this.updateOpportunityControlState();
  }
  get isCollaborativeReadOnly(): boolean {
    return this._isCollaborativeReadOnly;
  }
  public linkUnlinkFormGroup!: FormGroup<ILinkUnlinkFormGroup>;

  cultureId: number = CONSTANTS.ONE;
  estimateInfo!: EstimateInfoForOpportunity;
  opportunityModel!: OpportunityModel;

  private lastVerificationResult: OpportunityVerificationResponse | null = null;
  private isOpportunityLinked = false;
  isProspectAccount = false;

  estimateButtonStates: ISearchVerifyButtonState = { searchDisabled: false, verifyDisabled: false };

  verifiedFields = {
    estimateId: false,
    opportunityId: false
  };

  estimateDisplay = {
    name: '', salesOffice: '', businessUnit: '', segment: '', subSegment: '', gckCode: ''
  };

  opportunityDisplay = {
    name: '', salesOffice: '', governmentEntity: '', governmentAgency: '',
    buyAmerican: '', contractSchedule: '', dpas: '', cmmcLevel: '',
    gckCode: '', businessLine: '', segment: '', businessHierarchy: '',
    expectedBook: '', sellAs: '', ecs: '', restrictedType: '', salesOfficeNum: ''
  };

  private readonly destroy$ = new Subject<void>();
  private readonly resubscribe$ = new Subject<void>();
  private isSpinnerActive = false;

  constructor(
    private readonly translate: TranslateService,
    private readonly crmOperations: CrmOperationsService,
    private readonly fb: NonNullableFormBuilder,
    private readonly dialog: MatDialog,
    private readonly userProfileService: UserProfileService,
    private readonly notificationService: NotificationMessageService,
    private readonly confirmationService: ConfirmationDialogService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly loadingSpinnerService: LoadingSpinnerService
  ) {}

  // #region ─── Public Getters ───

  get linkUnlinkButtonLabel(): string {
    return this.isOpportunityLinked ? 'UNLINK_OPPORTUNITY' : 'LINK_OPPORTUNITY';
  }

  get canLinkUnlink(): boolean {
    if (this.isCollaborativeReadOnly) {
      return false;
    }
    if (this.isOpportunityLinked) {
      return this.verifiedFields.estimateId;
    }
    return (!this.crmOperations.isEstimateSubmitted()) && this.verifiedFields.estimateId && this.verifiedFields.opportunityId;
  }

  get isOpportunityDisabled(): boolean {
    return this.isCollaborativeReadOnly
      || !this.crmOperations.isEstimateOpened()
      || this.crmOperations.isEstimateSubmitted()
      || !this.verifiedFields.estimateId
      || this.isOpportunityLinked;
  }

  get isOpportunitySearchDisabled(): boolean {
    return !this.crmOperations.isEstimateOpened()
      || this.isCollaborativeReadOnly
      || this.crmOperations.isEstimateSubmitted()
      || !this.verifiedFields.estimateId
      || this.isOpportunityLinked;
  }

  get isOpportunityVerifyDisabled(): boolean {
    return !this.crmOperations.isEstimateOpened()
      || this.isCollaborativeReadOnly
      || this.crmOperations.isEstimateSubmitted()
      || !this.verifiedFields.estimateId
      || this.isOpportunityLinked
      || !this.linkUnlinkFormGroup?.controls.opportunityId.value?.trim()
      || this.verifiedFields.opportunityId;
  }

  get canShowFederalFundedSection(): boolean {
    return this.cultureId === CONSTANTS.ONE && !this.isChangeOrder;
  }

  get isChangeOrder(): boolean {
    return this.estimateInfo?.type === EstimateTypes.ChangeOrderEstimate;
  }

  // #endregion

  // #region ─── Lifecycle & Actions ───

  ngOnInit(): void {
    this.cultureId = this.crmOperations.getCultureId();
    this.estimateInfo = this.crmOperations.getEstimateInfoForOpportunity();
    this.opportunityModel = new OpportunityModel();
    this.initializeFormGroup();
    this.subscribeToIdChanges();
    
    if (this.estimateInfo?.estimateId) {
      this.estimateButtonStates = { searchDisabled: true, verifyDisabled: true };
      this.verifiedFields.estimateId = true;
      this.isProspectAccount = this.lastVerificationResult?.showProspectAccountWarning ?? false;
    } else {
      this.estimateButtonStates.verifyDisabled = true;
    }

    this.computeEstimateDisplay();
    this.computeOpportunityDisplay();
    this.updateOpportunityControlState();
    this.fetchOpportunityIfLinked();
  }

  ngOnDestroy(): void {
    this.resubscribe$.next();
    this.resubscribe$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchEstimate(): void {
    const dialogRef = this.dialog.open(SearchEstimateDialogComponent, {
      width: CONSTANTS.DIALOG_WIDTH_LARGE,
      height: CONSTANTS.DIALOG_HEIGHT_LARGE,
      disableClose: true,
      panelClass:'search-estimate-dialog'
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((estimateId: string) => {
        if (estimateId) {
          this.linkUnlinkFormGroup.controls.estimateId.setValue(estimateId, { emitEvent: false });
          this.verifyEstimate();
        }
      });
  }

  verifyEstimate(): void {
    const isEstimateOpen = !!this.estimateInfo?.estimateId;
    const estimateId = this.linkUnlinkFormGroup.controls.estimateId.value;
    if (!estimateId) {
      return;
    }

    this.crmOperations.verifyEstimateId(estimateId, this.userProfileService.userInfo?.mail ?? '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (info) => {
          this.estimateInfo = info;
          this.verifiedFields.estimateId = true;
          this.estimateButtonStates = { searchDisabled: isEstimateOpen, verifyDisabled: true };
          this.computeEstimateDisplay();
          this.updateOpportunityControlState();
          this.fetchOpportunityIfLinked();
        },
        error: async (error) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }

          this.verifiedFields.estimateId = false;
          this.computeEstimateDisplay();
        }
      });
  }

  verifyOpportunity(): void {
    const opportunityId = this.linkUnlinkFormGroup.controls.opportunityId.value.trim();
    if (!opportunityId) {
      return;
    }

    const request : VerifyOpportunityRequest = {
      opportunityId: opportunityId,
      cultureId: this.cultureId,
      estimateInfo: this.estimateInfo
    };

    this.crmOperations.verifyOpportunityCanLink(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // Handle blocking errors first — if any of these are true, do not mark opportunity as verified
          const blockingErrors: Array<{ condition: boolean; message: string }> = [
            { condition: result.showEstimateSubSegmentError, message: 'OPP_VERIFY_SUBSEGMENT_MISMATCH_ERROR' },
            { condition: result.showBookingClassificationError, message: 'OPP_VERIFY_BOOKING_CLASSIFICATION_ERROR' },
            { condition: result.showSoldToAccountMissingError, message: 'OPP_VERIFY_SOLDTO_ACCOUNT_MISSING_ERROR' },
            { condition: result.showChangeOrderDepthStructureError, message: 'OPP_VERIFY_CHANGE_ORDER_DEPTH_STRUCTURE_ERROR' },
          ];

          const blockingError = blockingErrors.find(e => e.condition);
          if (blockingError) {
            this.acceptOpportunityVerification(result, false);
            this.notificationService.showError(this.translate.instant(blockingError.message));
            return;
          }

          // Handle non-blocking warnings/info — show message but continue
          if (result.isCMMCUnmatched) {
            this.notificationService.showInfo(this.translate.instant('OPP_VERIFY_CMMC_UNMATCHED_INFO'));
          }

          if (result.showProspectAccountWarning) {
            this.isProspectAccount = true;
          }

          // Base order depth structure mismatch — requires user confirmation (Yes/No)
          if (result.showBaseOrderDepthStructureWarning) {
            this.confirmationService.confirmWarning(
              this.translate.instant('OPP_VERIFY_BASE_ORDER_DEPTH_STRUCTURE_WARNING')
            )
              .pipe(takeUntil(this.destroy$))
              .subscribe((confirmed) => {
                if (confirmed) {
                  this.acceptOpportunityVerification(result, true);
                }
                else{
                  this.acceptOpportunityVerification(result, false);
                }
              });
            return;
          }

          this.acceptOpportunityVerification(result, true);
        },
        error: () => {
          this.verifiedFields.opportunityId = false;
          this.opportunityModel = new OpportunityModel();
          this.computeOpportunityDisplay();
        }
      });
  }

  searchOpportunity(): void {
    const dialogRef = this.dialog.open(SearchOpportunityDialogComponent, {
      width: CONSTANTS.DIALOG_WIDTH_MEDIUM,
      disableClose: true
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId: string) => {
        if (opportunityId) {
          this.linkUnlinkFormGroup.controls.opportunityId.setValue(opportunityId, { emitEvent: false });
          this.verifyOpportunity();
        }
      });
  }

  linkOrUnlinkOpportunity(): void {
    if (this.isOpportunityLinked) {
      this.executeUnlinkOpportunity();
      return;
    }
    this.linkOpportunity();
  }

  linkOpportunity(): void {
    if (!this.verifiedFields.estimateId || !this.verifiedFields.opportunityId) {
      return;
    }

    const result = this.lastVerificationResult;

    // Check if opportunity is already linked to another estimate
    if (result?.isLinkedToOtherUser && result.existingAssociation) {
      const msg = this.translate.instant('OPP_VERIFY_LINKED_TO_OTHER_USER_ERROR', {
        estimateId: result.existingAssociation.estimateId,
        owner: result.existingAssociation.ownerName,
        userId: result.existingAssociation.userId
      });
      this.notificationService.showError(msg);
      return;
    }

    if (result?.isLinkedToCurrentUser && result.existingAssociation) {
      const msg = this.translate.instant('OPP_VERIFY_LINKED_TO_CURRENT_USER_CONFIRM', {
        estimateId: result.existingAssociation.estimateId,
        owner: result.existingAssociation.ownerName
      });
      this.confirmationService.confirmWarning(msg)
        .pipe(takeUntil(this.destroy$))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.executeLinkOpportunity();
          }
        });
      return;
    }

    this.executeLinkOpportunity();
  }

  closeDialog(): void {
    this.estimateInfo = this.crmOperations.getEstimateInfoForOpportunity();
    this.opportunityModel = new OpportunityModel();

    this.resubscribe$.next();
    this.initializeFormGroup();
    this.subscribeToIdChanges();

    this.verifiedFields = { estimateId: false, opportunityId: false };
    this.estimateButtonStates = { searchDisabled: false, verifyDisabled: false };
    this.isOpportunityLinked = false;
    this.lastVerificationResult = null;
    this.isProspectAccount = false;

    if (this.estimateInfo?.estimateId) {
      this.estimateButtonStates = { searchDisabled: true, verifyDisabled: true };
      this.verifiedFields.estimateId = true;
      this.linkUnlinkFormGroup.controls.estimateId.disable({ emitEvent: false });
    }
    this.computeEstimateDisplay();
    this.computeOpportunityDisplay();
    this.updateOpportunityControlState();
    this.fetchOpportunityIfLinked();
    this.cancel.emit();
  }

  // #endregion

  // #region ─── Private Methods ───

  private executeLinkOpportunity(): void {
    const userInfo = this.userProfileService.userInfo;
    const userId = userInfo?.gid ?? '';
    const userName = userInfo?.display_name ?? '';
    const userEmail = userInfo?.mail ?? '';

    this.startSpinner();

    this.crmOperations.linkOpportunity(
      this.estimateInfo,
      this.opportunityModel,
      userId,
      userName,
      userEmail
    )
      .pipe(takeUntil(this.destroy$), finalize(() => this.stopSpinner()))
      .subscribe({
        next: (event: LinkUnlinkOpportunityEvent) => {
            if (event.type === 'status') {
              this.loadingSpinnerService.updateMessage(event.status ?? '');
              return;
            }

            this.notificationService.showSuccess(this.translate.instant('OPP_LINK_SUCCESS_MSG'));
            this.estimateInfo.opportunityId = this.opportunityModel.opportunityId;
            this.isOpportunityLinked = true;
            this.isProspectAccount = this.lastVerificationResult?.showProspectAccountWarning ?? false;
            this.updateOpportunityControlState();
            this.crmOperations.syncEstimateDataIfOpen(event.result!);
        },
        error: async (error: unknown) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }

          this.notificationService.showError(this.translate.instant('OPP_LINK_FAILED_MSG'));
          this.isProspectAccount = false;
        }
      });
  }

  private executeUnlinkOpportunity(): void {
    const userInfo = this.userProfileService.userInfo;
    const userId = userInfo?.gid ?? '';
    const userName = userInfo?.display_name ?? '';
    const userEmail = userInfo?.mail ?? '';

    this.startSpinner();

    this.crmOperations.unlinkOpportunity(
      this.estimateInfo,
      this.opportunityModel,
      userId,
      userName,
      userEmail
    )
      .pipe(takeUntil(this.destroy$), finalize(() => this.stopSpinner()))
      .subscribe({
        next: (event: LinkUnlinkOpportunityEvent) => {
            if (event.type === 'status') {
              this.loadingSpinnerService.updateMessage(event.status ?? '');
              return;
            }

            this.notificationService.showSuccess(this.translate.instant('OPP_UNLINK_SUCCESS_MSG'));
            this.estimateInfo.opportunityId = '';
            this.isOpportunityLinked = false;
            this.isProspectAccount = false;
            this.lastVerificationResult = null;
            this.verifiedFields.opportunityId = false;
            this.linkUnlinkFormGroup.controls.opportunityId.setValue('', { emitEvent: false });
            this.linkUnlinkFormGroup.controls.opportunityId.enable({ emitEvent: false });
            this.opportunityModel = new OpportunityModel();
            this.computeOpportunityDisplay();
            this.crmOperations.syncEstimateDataIfOpen(event.result!);
        },
        error: async (error: unknown) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }

          this.notificationService.showError(this.translate.instant('OPP_UNLINK_FAILED_MSG'));
        }
      });
  }

  private stopSpinner(): void {
    if (!this.isSpinnerActive) {
      return;
    }
    this.isSpinnerActive = false;
    this.loadingSpinnerService.updateMessage('');
    this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
  }

  private startSpinner(): void {
    if (this.isSpinnerActive) {
      return;
    }
    this.isSpinnerActive = true;
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);
  }

  private acceptOpportunityVerification(result: OpportunityVerificationResponse, isVerified: boolean = false): void {
    this.lastVerificationResult = result;
    this.opportunityModel = result.opportunity;
    this.verifiedFields.opportunityId = isVerified;
    this.computeOpportunityDisplay();
  }

  private initializeFormGroup(): void {
    this.linkUnlinkFormGroup = this.fb.group<ILinkUnlinkFormGroup>({
      estimateId: this.fb.control({ value: this.estimateInfo?.estimateId ?? '', disabled: this.crmOperations.isEstimateOpened() }),
      opportunityId: this.fb.control({ value: this.opportunityModel?.opportunityId ?? '', disabled: !this.crmOperations.isEstimateOpened() || this.crmOperations.isEstimateSubmitted() || this.isCollaborativeReadOnly })
    });
  }

  private computeEstimateDisplay(): void {
    const e = this.estimateInfo;
    const unknown = this.translate.instant('UNKNOWN');
    const verified = this.verifiedFields.estimateId;

    const label = (value: string | undefined): string => verified ? (value || '') : unknown;   

    this.estimateDisplay = {
      name: label(e?.estimateName),
      salesOffice: label(e?.salesOffice),
      businessUnit: label(e?.businessUnit),
      segment: label(e?.segment),
      subSegment: label(e?.subSegment),
      gckCode: label(e?.gckCode)
    };
  }

  private computeOpportunityDisplay(): void {
    const o = this.opportunityModel;
    const unknown = this.translate.instant('UNKNOWN');
    const showDetails = this.verifiedFields.opportunityId || o.opportunityId;

    const label = (value: string | undefined): string => showDetails ? (value || '') : unknown;

    let restrictedType = '';
    if (!showDetails) {
      restrictedType = unknown;
    } else if (o?.isRestrictedOpportunity) {
      restrictedType = o.isRestrictedOpportunity.toUpperCase() === CONSTANTS.RESTRICTED_INDICATOR
        ? this.translate.instant('RESTRICTED')
        : this.translate.instant('UNRESTRICTED');
    }

    this.opportunityDisplay = {
      name: label(o?.opportunityName),
      salesOffice: label(o?.plSalesOffice),
      salesOfficeNum: label(o?.oppSalesOfficeNum),
      governmentEntity: label(o?.governmentEntity || o?.usSpecificInformation?.govtEntity),
      governmentAgency: label(o?.governmentAgency || o?.usSpecificInformation?.govtAgency),
      buyAmerican: label(o?.usSpecificInformation?.buyAmerican ?? o?.buyAmerican),
      contractSchedule: label(o?.usSpecificInformation?.contractSchedule),
      dpas: label(o?.usSpecificInformation?.dpas),
      cmmcLevel: label(o?.usSpecificInformation?.cmmcLevel),
      gckCode: label(o?.gckCode),
      businessLine: label(o?.plSegment),
      segment: label(o?.opportunityType),
      businessHierarchy: label(o?.plSubSegment),
      expectedBook: label(o?.closeDate),
      sellAs: showDetails ? (o?.cEstimateSalePrice || '') : unknown,
      ecs: showDetails
        ? (o?.bsesNationalAccts === CONSTANTS.Y ? this.translate.instant('YES') : this.translate.instant('NO'))
        : unknown,
      restrictedType
    };
  }

  private updateOpportunityControlState(): void {
    const opportunityControl = this.linkUnlinkFormGroup?.controls.opportunityId;
    if (!opportunityControl) {
      return;
    }

    if (this.isOpportunityDisabled) {
      opportunityControl.disable({ emitEvent: false });
    } else {
      opportunityControl.enable({ emitEvent: false });
    }
  }

  private fetchOpportunityIfLinked(): void {
    const opportunityId = this.estimateInfo?.opportunityId;
    if (!opportunityId || !this.verifiedFields.estimateId) {
      this.isOpportunityLinked = false;
      return;
    }

    const request: OpportunityRequest = {
      opportunityId,
      cultureId: this.cultureId
    };

    this.crmOperations.getOpportunity(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (opportunity) => {
          this.opportunityModel = opportunity;
          this.isOpportunityLinked = true;
          this.linkUnlinkFormGroup.controls.opportunityId.setValue(opportunityId, { emitEvent: false });
          this.linkUnlinkFormGroup.controls.opportunityId.disable({ emitEvent: false });
          this.verifiedFields.opportunityId = true;
          this.isProspectAccount= this.checkProspectAccount(opportunity);
          this.computeOpportunityDisplay();
        },
        error: () => {
          this.isOpportunityLinked = false;
          this.verifiedFields.opportunityId = false;
          this.isProspectAccount = false;
        }
      });
  }
private checkProspectAccount(opportunity: OpportunityModel): boolean {
  return opportunity.soldToSAPNumber === '';
}
  private subscribeToIdChanges(): void {
    this.linkUnlinkFormGroup.controls.estimateId.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe(() => {
        const isEstimateIdEmpty = !this.linkUnlinkFormGroup.controls.estimateId.value;
        this.estimateButtonStates = { searchDisabled: false, verifyDisabled: isEstimateIdEmpty };
        this.verifiedFields.estimateId = false;
        this.computeEstimateDisplay();

        // Clear opportunity data when estimate changes
        this.opportunityModel = new OpportunityModel();
        this.isOpportunityLinked = false;
        this.isProspectAccount = false;
        this.lastVerificationResult = null;
        this.verifiedFields.opportunityId = false;
        this.linkUnlinkFormGroup.controls.opportunityId.setValue('', { emitEvent: false });
        this.computeOpportunityDisplay();
        this.updateOpportunityControlState();
      });

    this.linkUnlinkFormGroup.controls.opportunityId.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe(() => {
        this.verifiedFields.opportunityId = false;
        this.isOpportunityLinked = false;
        this.isProspectAccount = false;
        this.lastVerificationResult = null;
        this.computeOpportunityDisplay();
      });
  }
  // #endregion
}
